import React, { useEffect, useState } from 'react'
import { isLoaded } from 'flux-entities'
import { Link } from 'react-router-dom'
import { IconButton, Container, Row, Col, Icons } from '@doseme/cohesive-ui'
import { DARKGREY } from '@doseme/cohesive-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTrash } from '@fortawesome/free-solid-svg-icons'

import { TProps } from './types'
import { IconBadge } from '../../../../components/Badge/IconBadge'
import { LabelBadge } from '../../../../components/Badge/LabelBadge'
import { Modal } from '../../../../components/Modal/Modal'
import { ConfirmModal } from '../../../../components/ConfirmModal/ConfirmModal'
import { PLANS_URL, plansEdit } from '../../../../constants/urls/frontend'
import { groupModels, TModelsWithEntitlements, IModelWithEntitlements } from './groupModels'
import { splitInHalf } from '../../../../utils/arrayUtils'
import './index.scss'
import { LoadingModal } from '../../../../components/LoadingModal'

const Show: React.FC<TProps> = (props) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const {
    drugs,
    fetchDrugs,
    plans,
    fetchPlans,
    deletePlan,
    history,
    match,
  } = props

  useEffect(() => {
    if (!plans.touched) { fetchPlans() }
  }, [fetchPlans, plans.touched])

  useEffect(() => {
    if (!drugs.touched) { fetchDrugs() }
  }, [fetchDrugs, drugs.touched])

  if (!isLoaded(props.plans)) {

    return <LoadingModal/>
  }

  const plan = plans.all[match.params.id]

  if (!plan) {
    return (
      <LoadingModal
        error={`A plan with id ${match.params.id} could not be found`}
      />
    )
  }

  const redirect = () => {
    history.push(PLANS_URL)
  }

  const handleDelete = async () => {
    await deletePlan(plan)
    setShowDeleteModal(false)
    history.push(PLANS_URL)
  }

  const confirmDeleteModalBody = (
    <ConfirmModal
      entityType='plan'
      entityName={plan.name}
      cancel={() => setShowDeleteModal(false)}
      confirm={handleDelete}
    />
  )
  const confirmDeleteModal = (
    <Modal
      type='confirm'
      show={showDeleteModal}
      onHide={() => setShowDeleteModal(false)}
      body={confirmDeleteModalBody}
    />
  )

  const modalHeader = (
    <section id='top'>
      <Container>
        <Row>
          <Col>
            <h3 className='co-h3'>{plan.name}</h3>
            <h3 className='co-h3 subscription-type'>{plan.type} Subscription</h3>
          </Col>
          <Col className='d-flex align-items-center justify-content-end'>
            <Link to={plansEdit(plan.id)}>
              <Icons.EditIconButton />
            </Link>

            <IconButton
              className='ml-2'
              background='white'
              data-testid='delete-icon'
              color={DARKGREY}
              size='30px'
              onClick={() => setShowDeleteModal(true)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </IconButton>
          </Col>
        </Row>
      </Container>
    </section>
  )

  const { firstGroup, secondGroup, unvalidatedGroup } = groupModels(
    plan,
    drugs.ids.map(x => ({
      module: drugs.all[x].module.toString(),
      name: drugs.all[x].name.toString()
    }))
  )

  const badge = (entitlements: number) => {
    if (entitlements === -1) {
      return (
        <IconBadge icon={faCheck} />
      )
    }

    if (entitlements > 0) {
      return <LabelBadge label={entitlements} />
    }
  }

  const renderDrugModule = (model: IModelWithEntitlements) => {
    return (
      <small key={model.module}>
        <div className='drug-module d-flex justify-content-between py-1'>
          <div>{model.name}:</div>
          {badge(model.entitlements)}
        </div>
      </small>
    )
  }

  const renderDrugGroup = (group: TModelsWithEntitlements): JSX.Element[] => {
    return Object.keys(group).map(drug =>
      <div key={drug}>
        <h6 className='co-h6 py-2'>{drug}</h6>
        {
          group[drug].map(renderDrugModule)
        }
      </div>
    )
  }

  const [unvalidatedC1, unvalidatedC2] = splitInHalf(unvalidatedGroup)

  const modalBody = (
    <section id='middle' className='show-plans'>
      <h4 className='co-h4'>Plan Summary</h4>
      <div className='ui-card mb-5 mt-1'>
        <Container>
          <Row>
            <Col className='pr-0'><strong>Plan Name:</strong></Col>
            <Col>{plan.name}</Col>
          </Row>
          <Row>
            <Col className='pr-0'><strong>Plan Type:</strong></Col>
            <Col>{plan.type}</Col>
          </Row>
          {
            plan.description && <Row>
              <Col className='pr-0'><strong>Description:</strong></Col>
              <Col>{plan.description}</Col>
            </Row>
          }
        </Container>
      </div>
      <h4 className='co-h4'>Plan Entitlements</h4>

      <div className='ui-card mt-1'>
        <Container>
          <Row>
            <div id='allowed'>
              <strong>Drug Entitlements Allowed (from plans):</strong>
            </div>
          </Row>

          <Row className='expand-card'>
            <Col>
            <h5 className='co-h5'>A - M</h5>
            {
              renderDrugGroup(firstGroup)
            }
            </Col>

            <Col>
            <h5 className='co-h5'>N - Z</h5>
            {
              renderDrugGroup(secondGroup)
            }
            </Col>
          </Row>

          <hr />

          <h5 className='co-h5'>Unvalidated</h5>
          <Row>
            <Col>
              {unvalidatedC1.map(renderDrugModule)}
            </Col>

            <Col>
              {unvalidatedC2.map(renderDrugModule)}
            </Col>
          </Row>
        </Container>
      </div>
    </section>
  )

  return (
    <>
      {confirmDeleteModal}

      <Modal
        type='regular'
        show={true}
        onHide={redirect}
        header={modalHeader}
        body={modalBody}
      >

    </Modal>
    </>
  )
}

export {
  Show
}
